/**
 * Dashboard Feed Hook
 * Combines high-priority tech requests and recent wins for the Activity Feed.
 *
 * (Internal messaging was retired Jul 2026 — the compose UI had already been
 * removed as dead code, so the feed's message source could only ever surface
 * stale pre-retirement messages. The 'message' member of the item-type union
 * is gone with it.)
 */

import { useMemo } from 'react';
import type { Timestamp } from 'firebase/firestore';
import { useOpenTechRequests } from './useTechRequests';
import { useDealsByStage } from './useDeals';
import { DealStage, TechRequestPriority } from '../types/theo-workflow.types';

export interface DashboardFeedItem {
  id: string;
  type: 'tech_request' | 'deal_won';
  priority?: TechRequestPriority | 'urgent' | 'normal';
  title: string;
  subtitle?: string;
  timestamp: string | Date | Timestamp; // ISO string, JS Date, or Firestore Timestamp depending on source
  metadata?: unknown; // source record (TechRequest | Deal) — set but not read by consumers
}

/**
 * Custom hook for the dashboard activity feed
 * Returns:
 * - High priority tech requests (at the top)
 * - Recent won deals
 */
export function useDashboardFeed() {
  const { requests: openRequests, loading: requestsLoading } = useOpenTechRequests();
  const { deals: wonDeals, loading: dealsLoading } = useDealsByStage(DealStage.WON);

  const feedItems = useMemo(() => {
    const items: DashboardFeedItem[] = [];

    // Add HIGH priority tech requests
    const highPriorityRequests = openRequests.filter(
      (req) => req.priority === TechRequestPriority.HIGH
    );

    highPriorityRequests.forEach((request) => {
      items.push({
        id: request.id!,
        type: 'tech_request',
        priority: request.priority,
        title: 'URGENT: Tech Support Needed',
        subtitle: request.question,
        timestamp: request.createdAt,
        metadata: request,
      });
    });

    // Add recent won deals (last 10)
    const recentWins = wonDeals
      .sort((a, b) => {
        const aTime = a.wonAt?.toMillis() || 0;
        const bTime = b.wonAt?.toMillis() || 0;
        return bTime - aTime;
      })
      .slice(0, 10);

    recentWins.forEach((deal) => {
      items.push({
        id: deal.id!,
        type: 'deal_won',
        title: `Deal Won: ${deal.title}`,
        subtitle: deal.category || 'Uncategorized',
        timestamp: deal.wonAt || deal.updatedAt,
        metadata: deal,
      });
    });

    return items;
  }, [openRequests, wonDeals]);

  // Separate high priority items (high priority tech requests) for special styling
  const highPriorityItems = feedItems.filter(
    (item: DashboardFeedItem) =>
      item.type === 'tech_request' && item.priority === TechRequestPriority.HIGH
  );

  const regularItems = feedItems.filter(
    (item: DashboardFeedItem) =>
      !(item.type === 'tech_request' && item.priority === TechRequestPriority.HIGH)
  );

  return {
    feedItems,
    highPriorityItems,
    regularItems,
    loading: requestsLoading || dealsLoading,
    hasHighPriorityItems: highPriorityItems.length > 0,
  };
}
